import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { SilaeCodeWizard } from ".";
import { CodeRepositoryView } from "../CodeRepositoryView";
import { CreateCodeDialog } from "../CreateCodeDialog";
import { createGeneratedCode } from "@/services/generatedCodeService";
import { toast } from "sonner";
import { useAppDispatch } from "@/hooks/redux";
import { resetForm } from "@/store/slices/codeGenerationSlice";
import { useAvailableTags } from "@/hooks/useAvailableTags";
import {
  createCodeValidationSchema,
  createCodeInitialValues,
} from "@/schemas/createCodeSchema";
import type { CreateCodeFormValues } from "@/types/generatedCode";
import { extractErrorMessage } from "@/types/generatedCode";

/**
 * WorkspaceTab
 *
 * Main workspace tab for code generation feature.
 * - Shows history of generated codes by default
 * - Has a "Create New Code" button that opens a dialog
 * - Creates the code in DB immediately with name/description
 * - Opens wizard to edit the code
 * - Can navigate back to history from wizard
 */
export const WorkspaceTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // UI State
  const [showWizard, setShowWizard] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [currentCodeId, setCurrentCodeId] = useState<string | null>(null);
  const [openAtValidation, setOpenAtValidation] = useState(false);

  // Load available tags (predefined + custom)
  const { allTags } = useAvailableTags();

  // Formik for form validation and management
  const formik = useFormik<CreateCodeFormValues>({
    initialValues: createCodeInitialValues,
    validationSchema: createCodeValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Reset Redux form BEFORE creating the code
        dispatch(resetForm());

        // Create the code in database with empty formData
        const newCode = await createGeneratedCode({
          name: values.name.trim(),
          description: values.description.trim() || undefined,
          formData: {
            structureType: "simple",
            hasExclusions: false,
            exclusions: [],
            exclusionsOperators: [],
            conditions: [],
            conditionsOperators: [],
            ifBranch: {
              bonuses: [],
            },
          },
          generatedCode: "",
          tags: values.tags,
          isTemplate: false,
        });

        // Store the created code ID and navigate to wizard
        setCurrentCodeId(newCode.id);
        setShowCreateDialog(false);
        setShowWizard(true);

        // Reset form after successful creation
        formik.resetForm();

        toast.success(
          t("codeGenerator.messages.codeCreated", { name: newCode.name }),
        );
      } catch (error: unknown) {
        console.error("❌ Error creating code:", error);

        // Utiliser la fonction helper pour extraire le message
        const errorMessage = extractErrorMessage(
          error,
          t("codeGenerator.errors.createFailed"),
        );

        toast.error(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Handle opening create dialog
  const handleOpenCreateDialog = useCallback(() => {
    formik.resetForm();
    setShowCreateDialog(true);
  }, [formik]);

  // Handle closing wizard and returning to history
  const handleCloseWizard = useCallback(() => {
    dispatch(resetForm());
    setShowWizard(false);
    setCurrentCodeId(null);
    setOpenAtValidation(false);
  }, [dispatch]);

  // Handle edit existing code
  const handleEditCode = useCallback(
    (codeId: string, hasGeneratedCode: boolean) => {
      setCurrentCodeId(codeId);
      setOpenAtValidation(hasGeneratedCode);
      setShowWizard(true);
    },
    [],
  );

  if (showWizard) {
    // Show the wizard
    return (
      <div className="flex flex-col h-full">
        {/* Wizard content */}
        <div className="flex-1 overflow-auto">
          <SilaeCodeWizard
            codeId={currentCodeId}
            openAtValidation={openAtValidation}
            onCodeSaved={handleCloseWizard}
            onFinish={handleCloseWizard}
            onCancel={handleCloseWizard}
          />
        </div>
      </div>
    );
  }

  // Show history by default
  return (
    <>
      <div className="flex flex-col h-full">
        {/* History content */}
        <div className="flex-1 overflow-auto p-6">
          <CodeRepositoryView
            onEdit={handleEditCode}
            onCreateNew={handleOpenCreateDialog}
          />
        </div>
      </div>

      {/* Create Code Dialog */}
      <CreateCodeDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        formik={formik}
        allTags={allTags}
      />
    </>
  );
};
